import toast from 'react-hot-toast';

import {
  Entity,
  CustomEndpoint,
  HttpMethod
} from '../../types/builder';

interface Props {
  entity: Entity;
  onUpdate: (entity: Entity) => void;
  maxCustomEndpoints: number;
  target: 'dotnet' | 'java';  
}


const METHODS: HttpMethod[] = [
  'GET', 'POST', 'PUT', 'DELETE', 'PATCH'
];

const METHOD_STYLES: Record<string, string> = {
  GET:    'bg-green-500/10 text-green-400 border-green-500/20',
  POST:   'bg-blue-500/10 text-blue-400 border-blue-500/20',
  PUT:    'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  DELETE: 'bg-red-500/10 text-red-400 border-red-500/20',
  PATCH:  'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const toPascalCase = (str: string) =>
  str.split(/[-_\/]/).filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');

const CustomEndpointsTab = ({
  entity, onUpdate,
  maxCustomEndpoints,
  target  
}: Props) => {

  const plural = entity.name.toLowerCase() + 's';
  const base = `/api/${plural}`;

  const addEndpoint = () => {

     if (entity.customEndpoints.length >= maxCustomEndpoints) {
    toast.error(
      `Maximum ${maxCustomEndpoints} custom endpoints per entity!`
    );
    return;
  }

    onUpdate({
      ...entity,
      customEndpoints: [
        ...entity.customEndpoints,
        {
          id: Date.now(),
          method: 'GET',
          path: '/custom',
          description: 'Custom endpoint'
        }
      ]
    });
  };

  const deleteEndpoint = (id: number) => {
    onUpdate({
      ...entity,
      customEndpoints: entity.customEndpoints
        .filter(ep => ep.id !== id)
    });
  };

  const updateEndpoint = (
    id: number,
    field: keyof CustomEndpoint,
    value: any
  ) => {
    onUpdate({
      ...entity,
      customEndpoints: entity.customEndpoints
        .map(ep =>
          ep.id === id
            ? { ...ep, [field]: value }
            : ep
        )
    });
  };

  return (
    <div>

      {/* Header */}
      <div className="flex items-center
                      justify-between mb-3">
        <span className="text-gray-500 text-xs">
          Add custom endpoints beyond CRUD:
        </span>
        <span className="bg-purple-500/10
                         text-purple-400
                         border border-purple-500/20
                         rounded-full px-2 py-0.5
                         text-xs font-mono">
          {entity.customEndpoints.length} custom
        </span>
      </div>

      {/* Empty State */}
      {entity.customEndpoints.length === 0 && (
        <div className="text-center py-6
                        border border-dashed
                        border-gray-700
                        rounded-xl mb-3">
          <div className="text-2xl mb-2">🔧</div>
          <p className="text-gray-500 text-sm">
            No custom endpoints yet
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Add search, export, bulk operations etc.
          </p>
        </div>
      )}

      {/* Custom Endpoint Rows */}
      {entity.customEndpoints.length > 0 && (
        <div className="mb-3">

          {/* Column Headers */}
          <div className="grid grid-cols-4 gap-2
                          pb-1 mb-1">
            {['Method', `Path (after ${base})`,
              'Description', '']
              .map(h => (
                <span key={h}
                      className="text-gray-600
                                 font-mono text-xs
                                 uppercase
                                 tracking-wider">
                  {h}
                </span>
              ))}
          </div>

          {/* Rows */}
          {entity.customEndpoints.map(ep => (
            <div key={ep.id}
                 className="grid grid-cols-4
                            gap-2 py-1.5
                            items-center
                            border-b border-gray-800
                            last:border-0">

              {/* Method */}
              <select
                value={ep.method}
                onChange={e => updateEndpoint(
                  ep.id, 'method',
                  e.target.value as HttpMethod
                )}
                className={`bg-gray-800
                            border rounded-lg
                            px-2 py-1.5
                            text-xs font-mono
                            font-bold outline-none
                            ${METHOD_STYLES[ep.method]}`}>
                {METHODS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>

              {/* Path */}
              <input
                value={ep.path}
                onChange={e => updateEndpoint(
                  ep.id, 'path', e.target.value
                )}
                className="bg-gray-800
                           border border-gray-700
                           text-gray-200
                           rounded-lg px-2 py-1.5
                           text-xs font-mono
                           outline-none
                           focus:border-blue-500"
                placeholder="/search"
              />

              {/* Description */}
              <input
                value={ep.description}
                onChange={e => updateEndpoint(
                  ep.id, 'description',
                  e.target.value
                )}
                className="bg-gray-800
                           border border-gray-700
                           text-gray-300
                           rounded-lg px-2 py-1.5
                           text-xs outline-none
                           focus:border-blue-500"
                placeholder="Description"
              />

              {/* Delete */}
              <button
                onClick={() => deleteEndpoint(ep.id)}
                className="text-gray-600
                           hover:text-red-400
                           transition-colors
                           text-sm text-center">
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Button */}
      <button
        onClick={addEndpoint}
        className="w-full border border-dashed
                   border-gray-700
                   hover:border-purple-500
                   text-gray-600
                   hover:text-purple-400
                   rounded-lg py-2
                   text-xs font-mono
                   transition-colors">
        + Add Custom Endpoint
      </button>

     {/* Code Preview */}
{entity.customEndpoints.length > 0 && (
  <div className="mt-4 bg-gray-800/50
                  border border-gray-700
                  rounded-xl p-4">

    {/* Preview Header */}
    <div className="text-gray-600
                    font-mono text-xs
                    uppercase tracking-wider
                    mb-3 flex items-center
                    justify-between">
      <span>
        {target === 'dotnet'
          ? 'Generated .NET 8 Code Preview:'
          : 'Generated Java Spring Boot (Maven) Preview:'
        }
      </span>
      <span className={`text-xs px-2 py-0.5
                        rounded-full border
                        ${target === 'dotnet'
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                          : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                        }`}>
        {target === 'dotnet'
          ? '.NET 8'
          : 'Spring Boot'
        }
      </span>
    </div>

       {/* Code Preview*/}
        {entity.customEndpoints.map(ep => (
          <div key={ep.id}
              className="mb-4 font-mono
                          text-xs leading-relaxed">

            {target === 'dotnet' ? (
              // .NET 8 Preview
              <>
                <span className="text-blue-400">
                  [Http{ep.method.charAt(0) +
                    ep.method.slice(1).toLowerCase()}
                  ("{ep.path.replace('/', '')}")]
                </span>
                <br/>
                <span className="text-gray-300">
                  public async Task&lt;IActionResult&gt;{' '}
                  {toPascalCase(ep.path)}()
                </span>
                <br/>
                <span className="text-gray-500">
                  {'{'}
                </span>
                <br/>
                <span className="text-gray-600 ml-4">
                  {'// '}{ep.description}
                </span>
                <br/>
                <span className="text-gray-600 ml-4">
                  throw new NotImplementedException();
                </span>
                <br/>
                <span className="text-gray-500">
                  {'}'}
                </span>
              </>
            ) : (
              // Java Spring Boot (Maven) Preview
              <>
                <span className="text-yellow-400">
                  @{ep.method.charAt(0) +
                    ep.method.slice(1).toLowerCase()}
                  Mapping("{ep.path}")
                </span>
                <br/>
                <span className="text-gray-300">
                  public ResponseEntity&lt;?&gt;{' '}
                  {ep.path
                    .replace('/', '')
                    .replace(/-([a-z])/g,
                      (_, c) => c.toUpperCase()
                    )
                  }()
                </span>
                <span className="text-gray-500">
                  {' {'}
                </span>
                <br/>
                <span className="text-gray-600 ml-4">
                  {'// '}{ep.description}
                </span>
                <br/>
                <span className="text-gray-600 ml-4">
                  throw new
                  UnsupportedOperationException();
                </span>
                <br/>
                <span className="text-gray-500">
                  {'}'}
                </span>
              </>
            )}
          </div>
        ))}
      </div>
    )}
    </div>
  );
};

export default CustomEndpointsTab;